import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A sliding category drawer for the custom header.
 *
 * The trigger button lives in the header, outside this element, and reaches it
 * through `on:click="#<drawer id>/open"`.
 *
 * @typedef {object} DrawerRefs
 * @property {HTMLElement} track - The sliding track that holds every view.
 * @property {HTMLElement[]} [view] - The main view plus one view per submenu.
 *
 * @extends {Component<DrawerRefs>}
 */
class CustomHeaderDrawer extends Component {
  requiredRefs = ['track'];

  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    document.addEventListener('keydown', this.#onKeyDown, { signal });

    if (Shopify.designMode) {
      document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
      document.addEventListener('shopify:block:deselect', this.#onBlockDeselect, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();

    // The drawer can be torn down while open, e.g. when the theme editor
    // re-renders the section, which would otherwise leave the page locked.
    if (this.isOpen) {
      unlockScroll();
      removeTrapFocus();
    }
  }

  /** @returns {boolean} Whether the drawer is open. */
  get isOpen() {
    return this.classList.contains('is-open');
  }

  /**
   * The button that controls this drawer, located by its `aria-controls`.
   *
   * @returns {HTMLElement | null}
   */
  get #trigger() {
    if (!this.id) return null;

    const trigger = document.querySelector(`[aria-controls="${this.id}"]`);

    return trigger instanceof HTMLElement ? trigger : null;
  }

  open() {
    if (this.isOpen) return;

    this.classList.add('is-open');
    this.removeAttribute('inert');
    this.#trigger?.setAttribute('aria-expanded', 'true');
    lockScroll();

    this.#activate('main');
  }

  close() {
    if (!this.isOpen) return;

    // Release the trap before making the drawer inert, otherwise the trap
    // fights the blur that `inert` triggers on the focused child.
    removeTrapFocus();

    this.classList.remove('is-open');
    this.setAttribute('inert', '');
    this.#trigger?.setAttribute('aria-expanded', 'false');
    unlockScroll();

    this.#trigger?.focus({ preventScroll: true });

    // Reset once the panel has slid out, so the track does not visibly snap
    // back to the main view mid-animation.
    requestAnimationFrame(() => {
      onAnimationEnd(this, () => {
        if (!this.isOpen) this.#activate('main', { focus: false });
      });
    });
  }

  /**
   * Open the submenu named by the clicked row's `data-drawer-submenu-target`.
   *
   * @param {Event} event
   */
  openSubmenu(event) {
    const { target } = event;
    const row = target instanceof Element ? target.closest('[data-drawer-submenu-target]') : null;
    const view = row instanceof HTMLElement ? row.dataset.drawerSubmenuTarget : null;

    if (view) this.#activate(view);
  }

  closeSubmenu() {
    const { view: all = [] } = this.refs;
    const active = all.find((view) => view.classList.contains('is-active'));
    const parent = active instanceof HTMLElement ? active.dataset.drawerParentView : null;

    this.#activate(parent || 'main');
  }

  /**
   * Slide the track to a view and make every other view inert.
   *
   * @param {string} name - The `data-drawer-view` value to activate.
   * @param {object} [options]
   * @param {boolean} [options.focus=true] - Whether to move the focus trap to the view.
   */
  #activate(name, { focus = true } = {}) {
    const { track, view: all = [] } = this.refs;
    const target = all.find((view) => view.dataset.drawerView === name);

    if (!target) return;

    for (const view of all) {
      const isActive = view === target;

      view.classList.toggle('is-active', isActive);
      // `inert` keeps the off-screen views out of both the tab order and the
      // accessibility tree, which `aria-hidden` alone would not do.
      view.inert = !isActive;
      view.scrollTop = 0;
    }

    for (const row of this.querySelectorAll('[data-drawer-submenu-target]')) {
      if (!(row instanceof HTMLElement)) continue;

      row.setAttribute('aria-expanded', String(row.dataset.drawerSubmenuTarget === name));
    }

    track.classList.toggle('is-submenu-open', name !== 'main');

    // offsetLeft is only meaningful once the view has been laid out.
    requestAnimationFrame(() => {
      track.style.setProperty('--custom-drawer-view-offset', `${target.offsetLeft * -1}px`);

      if (!focus) return;

      // Wait one more frame so the transition we just triggered is running by
      // the time getAnimations() is asked about it.
      requestAnimationFrame(() => this.#trapFocusIn(target));
    });
  }

  /**
   * Trap focus inside a view, but only once the drawer has finished moving.
   * Focusing an element mid-transition can scroll the page.
   *
   * @param {HTMLElement} view
   */
  #trapFocusIn(view) {
    onAnimationEnd(this, () => {
      if (!this.isOpen || !view.classList.contains('is-active')) return;

      trapFocus(view);
    });
  }

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;

    this.close();
  };

  // The block's shopify_attributes sit on the trigger button, so the editor
  // reports that button as the selected block.
  /** @param {Event} event */
  #onBlockSelect = (event) => {
    if (event.target === this.#trigger) this.open();
  };

  /** @param {Event} event */
  #onBlockDeselect = (event) => {
    if (event.target === this.#trigger) this.close();
  };
}

/**
 * The header search field, which expands a predictive search dropdown.
 *
 * This wraps `predictive-search-component` rather than replacing it, so it only
 * owns the open/closed state of the dropdown. Listeners are attached directly
 * instead of with `on:` attributes, because the input sits inside
 * `predictive-search-component` and declarative handlers would resolve to that
 * component instead of this one.
 *
 * @extends {Component}
 */
class CustomHeaderSearch extends Component {
  #controller = new AbortController();

  /**
   * Whether the shopper has done anything on the page yet.
   *
   * Predictive search focuses the input as a side effect of clearing its
   * selection index, which it does about a second after load while preloading
   * the empty state for shoppers with recently viewed products. In the theme's
   * own search modal that goes unnoticed, because the field is inside a closed
   * dialog, but this field is on the page, so the preload would take focus away
   * from wherever the shopper actually was. Focus arriving before any pointer
   * or key event cannot have been asked for, so it is refused outright.
   */
  #userHasInteracted = false;

  /** @returns {HTMLInputElement | null} */
  get #input() {
    const input = this.querySelector('input[type="search"]');

    return input instanceof HTMLInputElement ? input : null;
  }

  connectedCallback() {
    super.connectedCallback();

    const input = this.#input;

    if (!input) return;

    const { signal } = this.#controller;

    this.close();

    this.#refuseFocus(input);

    input.addEventListener('click', this.open, { signal });
    input.addEventListener('input', this.open, { signal });
    document.addEventListener('pointerdown', this.#onUserInteraction, { signal, capture: true });
    document.addEventListener('keydown', this.#onUserInteraction, { signal, capture: true });
    document.addEventListener('pointerdown', this.#onPointerDown, { signal });
    document.addEventListener('keydown', this.#onKeyDown, { signal });
    this.addEventListener('keydown', this.#onSearchKeyDown, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();

    const input = this.#input;

    if (input) this.#allowFocus(input);
  }

  /**
   * Makes focus() a no-op on the input until the shopper does something.
   *
   * Letting the focus land and blurring it afterwards is not enough: the
   * browser keeps its sequential focus navigation starting point on the element
   * that was blurred, so the next Tab resumes from the search field and skips
   * everything before it in the header. Refusing the call leaves the starting
   * point where it should be, at the top of the document.
   *
   * @param {HTMLInputElement} input
   */
  #refuseFocus(input) {
    Object.defineProperty(input, 'focus', { configurable: true, value: () => {} });
  }

  /** @param {HTMLInputElement} input */
  #allowFocus(input) {
    delete (/** @type {{ focus?: unknown }} */ (/** @type {unknown} */ (input))).focus;
  }

  open = () => {
    this.classList.add('is-search-open');
    this.#input?.setAttribute('aria-expanded', 'true');
  };

  close = () => {
    this.classList.remove('is-search-open');
    this.#input?.setAttribute('aria-expanded', 'false');
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.target instanceof Node && !this.contains(event.target)) this.close();
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    this.close();
  };

  /**
   * Forwards keys to the theme's predictive search, except Tab.
   *
   * The theme maps Tab onto result-list navigation and calls preventDefault on
   * it. That works in its search modal, where focus is trapped in a dialog and
   * Tab has nowhere else to go, but this field is inline, so the same handler
   * left keyboard users unable to leave it at all.
   *
   * The markup therefore leaves off the `on:keydown` binding that would
   * normally wire this up. That binding is delegated from a capture listener on
   * `document`, so it runs ahead of anything a descendant could do and offers
   * no way to opt out of a single key. Calling the handler ourselves lets Tab
   * through as ordinary focus movement and leaves every other key to the theme.
   *
   * @param {KeyboardEvent} event
   */
  #onSearchKeyDown = (event) => {
    if (event.key === 'Tab') return;

    const search = /** @type {{ onSearchKeyDown?: (event: KeyboardEvent) => void } | null} */ (
      /** @type {unknown} */ (this.querySelector('predictive-search-component'))
    );

    search?.onSearchKeyDown?.(event);
  };

  #onUserInteraction = () => {
    if (this.#userHasInteracted) return;

    this.#userHasInteracted = true;

    const input = this.#input;

    // Runs on pointerdown and keydown, both ahead of the focus the browser is
    // about to move as their default action, so a shopper clicking or tabbing
    // into the field still lands in it.
    if (input) this.#allowFocus(input);
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    const { relatedTarget } = event;

    // A null relatedTarget means focus left the document entirely, which should
    // not discard what the shopper was doing.
    if (!(relatedTarget instanceof Node) || this.contains(relatedTarget)) return;

    this.close();
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (event.target instanceof Element && event.target.closest('a')) this.close();
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    // Restoring from the back/forward cache also restores the open dropdown.
    if (event.persisted) this.close();
  };
}

/** Reuses the theme's `html[scroll-lock]` styling from base.css. */
function lockScroll() {
  document.documentElement.setAttribute('scroll-lock', '');
}

function unlockScroll() {
  document.documentElement.removeAttribute('scroll-lock');
}

/**
 * Desktop quick-links navigation with up to three menu levels.
 *
 * Independent of Horizon's header-menu so theme updates cannot break open /
 * close behavior. Open mode comes from `data-open-mode` (`hover` or `click`).
 *
 * @extends {Component}
 */
class CustomHeaderNav extends Component {
  #controller = new AbortController();

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeTimer;

  /** @returns {'hover' | 'click'} */
  get #openMode() {
    return this.dataset.openMode === 'click' ? 'click' : 'hover';
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    this.addEventListener('pointerover', this.#onPointerOver, { signal });
    this.addEventListener('pointerout', this.#onPointerOut, { signal });
    this.addEventListener('focusin', this.#onFocusIn, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    document.addEventListener('pointerdown', this.#onDocumentPointerDown, { signal });
    document.addEventListener('keydown', this.#onKeyDown, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    clearTimeout(this.#closeTimer);
  }

  /**
   * @param {HTMLElement} trigger
   * @returns {HTMLElement | null}
   */
  #submenuFor(trigger) {
    const id = trigger.getAttribute('aria-controls');
    const submenu = id ? this.querySelector(`#${CSS.escape(id)}`) : null;

    return submenu instanceof HTMLElement ? submenu : null;
  }

  /**
   * @param {HTMLElement} trigger
   * @returns {boolean}
   */
  #isOpen(trigger) {
    return trigger.getAttribute('aria-expanded') === 'true';
  }

  /**
   * @param {string | null} href
   * @returns {boolean}
   */
  #isNavigableHref(href) {
    if (!href) return false;

    const value = href.trim();

    return value !== '' && value !== '#' && value !== '/#';
  }

  /**
   * @param {Element} from
   * @returns {HTMLElement | null}
   */
  #triggerForItem(from) {
    const item = from.closest('.custom-header__nav-item--has-children, .custom-header__submenu-item--has-children');

    if (!(item instanceof HTMLElement) || !this.contains(item)) return null;

    const trigger = item.querySelector(
      ':scope > .custom-header__nav-control > [data-submenu-trigger], :scope > .custom-header__submenu-control > [data-submenu-trigger]'
    );

    return trigger instanceof HTMLElement ? trigger : null;
  }

  /**
   * @param {HTMLElement} trigger
   */
  #open(trigger) {
    const submenu = this.#submenuFor(trigger);

    if (!submenu) return;

    clearTimeout(this.#closeTimer);

    const item = trigger.closest('.custom-header__nav-item, .custom-header__submenu-item');
    const scope = item?.parentElement ?? this;

    for (const other of scope.querySelectorAll(
      ':scope > .custom-header__nav-item--has-children > .custom-header__nav-control > [data-submenu-trigger], :scope > .custom-header__submenu-item--has-children > .custom-header__submenu-control > [data-submenu-trigger]'
    )) {
      if (other instanceof HTMLElement && other !== trigger) this.#close(other, { nested: true });
    }

    trigger.setAttribute('aria-expanded', 'true');
    item?.classList.add('is-open');
    submenu.hidden = false;
  }

  /**
   * @param {HTMLElement} trigger
   * @param {{ nested?: boolean }} [options]
   */
  #close(trigger, { nested = false } = {}) {
    const submenu = this.#submenuFor(trigger);

    if (!submenu) return;

    trigger.setAttribute('aria-expanded', 'false');
    trigger.closest('.custom-header__nav-item, .custom-header__submenu-item')?.classList.remove('is-open');
    submenu.hidden = true;

    if (!nested) return;

    for (const childTrigger of submenu.querySelectorAll('[data-submenu-trigger]')) {
      if (childTrigger instanceof HTMLElement) this.#close(childTrigger, { nested: true });
    }
  }

  /** Close every open panel in this nav. */
  #closeAll() {
    clearTimeout(this.#closeTimer);

    for (const trigger of this.querySelectorAll('[data-submenu-trigger][aria-expanded="true"]')) {
      if (trigger instanceof HTMLElement) this.#close(trigger);
    }
  }

  /**
   * @param {Element} from
   * @returns {HTMLElement | null}
   */
  #nearestTrigger(from) {
    const trigger = from.closest('[data-submenu-trigger]');

    return trigger instanceof HTMLElement && this.contains(trigger) ? trigger : null;
  }

  /**
   * Open the menus that contain the hovered/focused node, and close nested
   * panels the pointer has left.
   *
   * @param {Element} from
   */
  #syncHoverTarget(from) {
    const nestedItem = from.closest('.custom-header__submenu-item--has-children');
    const topItem = from.closest('.custom-header__nav-item--has-children');

    if (topItem instanceof HTMLElement) {
      const topTrigger = topItem.querySelector(
        ':scope > .custom-header__nav-control > [data-submenu-trigger]'
      );

      if (topTrigger instanceof HTMLElement) this.#open(topTrigger);

      for (const nestedTrigger of topItem.querySelectorAll(
        ':scope > .custom-header__submenu > .custom-header__submenu-item--has-children > .custom-header__submenu-control > [data-submenu-trigger]'
      )) {
        if (!(nestedTrigger instanceof HTMLElement)) continue;

        if (nestedItem instanceof HTMLElement && nestedItem.contains(nestedTrigger)) {
          this.#open(nestedTrigger);
        } else {
          this.#close(nestedTrigger, { nested: true });
        }
      }

      return;
    }

    this.#closeAll();
  }

  /** @param {PointerEvent} event */
  #onPointerOver = (event) => {
    if (this.#openMode !== 'hover') return;
    if (!(event.target instanceof Element)) return;

    clearTimeout(this.#closeTimer);
    this.#syncHoverTarget(event.target);
  };

  /** @param {PointerEvent} event */
  #onPointerOut = (event) => {
    if (this.#openMode !== 'hover') return;

    const related = event.relatedTarget;

    if (related instanceof Node && this.contains(related)) return;

    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => this.#closeAll(), 150);
  };

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    if (this.#openMode !== 'hover') return;
    if (!(event.target instanceof Element)) return;

    this.#syncHoverTarget(event.target);
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    const { relatedTarget } = event;

    if (relatedTarget instanceof Node && this.contains(relatedTarget)) return;

    this.#closeAll();
  };

  /**
   * @param {HTMLElement} trigger
   */
  #toggle(trigger) {
    if (this.#isOpen(trigger)) {
      this.#close(trigger, { nested: true });
    } else {
      this.#open(trigger);
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const labelLink = event.target.closest('a.custom-header__nav-label, a.custom-header__submenu-label');

    // A real parent/child url always navigates; the menu is only opened by the
    // caret, non-link chrome, or hover.
    if (labelLink instanceof HTMLAnchorElement && this.#isNavigableHref(labelLink.getAttribute('href'))) {
      this.#closeAll();
      return;
    }

    const leafLink = event.target.closest('a.custom-header__submenu-link:not(.custom-header__submenu-label)');

    if (leafLink) {
      this.#closeAll();
      return;
    }

    const trigger = this.#nearestTrigger(event.target) ?? this.#triggerForItem(event.target);

    if (!trigger) return;

    // Caret, placeholder label, or other control chrome toggles the panel.
    event.preventDefault();
    this.#toggle(trigger);
  };

  /** @param {PointerEvent} event */
  #onDocumentPointerDown = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;

    this.#closeAll();
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    if (![...this.querySelectorAll('[data-submenu-trigger][aria-expanded="true"]')].length) return;

    event.stopPropagation();

    const openTriggers = [...this.querySelectorAll('[data-submenu-trigger][aria-expanded="true"]')].filter(
      (el) => el instanceof HTMLElement
    );

    /** @type {HTMLElement | undefined} */
    const deepest = openTriggers.at(-1);

    if (!deepest) return;

    this.#close(deepest, { nested: true });
    deepest.focus({ preventScroll: true });
  };
}

/**
 * Language picker that opens the same kind of panel as a quick-link submenu.
 *
 * Kept separate from `custom-header-nav` because the options submit a
 * localization form instead of navigating. Open/close follows that nav: click
 * the trigger, click outside, or Escape.
 *
 * @typedef {object} LanguageRefs
 * @property {HTMLInputElement} languageInput - Hidden `language_code` field.
 *
 * @extends {Component<LanguageRefs>}
 */
class CustomHeaderLanguage extends Component {
  #controller = new AbortController();

  /** @returns {HTMLButtonElement | null} */
  get #trigger() {
    const trigger = this.querySelector('[data-language-trigger]');

    return trigger instanceof HTMLButtonElement ? trigger : null;
  }

  /** @returns {HTMLElement | null} */
  get #menu() {
    const menu = this.querySelector('[data-language-menu]');

    return menu instanceof HTMLElement ? menu : null;
  }

  /** @returns {HTMLFormElement | null} */
  get #form() {
    const form = this.querySelector('form');

    return form instanceof HTMLFormElement ? form : null;
  }

  /** @returns {boolean} */
  get #isOpen() {
    return this.classList.contains('is-open');
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    this.addEventListener('click', this.#onClick, { signal });
    document.addEventListener('pointerdown', this.#onDocumentPointerDown, { signal });
    document.addEventListener('keydown', this.#onKeyDown, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #open() {
    const trigger = this.#trigger;
    const menu = this.#menu;

    if (!trigger || !menu || this.#isOpen) return;

    this.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    menu.hidden = false;
  }

  #close() {
    const trigger = this.#trigger;
    const menu = this.#menu;

    if (!trigger || !menu || !this.#isOpen) return;

    this.classList.remove('is-open');
    trigger.setAttribute('aria-expanded', 'false');
    menu.hidden = true;
  }

  #toggle() {
    if (this.#isOpen) {
      this.#close();
    } else {
      this.#open();
    }
  }

  /**
   * @param {string} code
   */
  #select(code) {
    const { languageInput } = this.refs;
    const form = this.#form;

    if (!form || !languageInput) return;

    if (languageInput.value === code) {
      this.#close();
      return;
    }

    languageInput.value = code;
    form.submit();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const option = event.target.closest('[data-language-code]');

    if (option instanceof HTMLElement && this.contains(option)) {
      event.preventDefault();
      this.#select(option.dataset.languageCode ?? '');
      return;
    }

    if (event.target.closest('[data-language-trigger]')) {
      event.preventDefault();
      this.#toggle();
    }
  };

  /** @param {PointerEvent} event */
  #onDocumentPointerDown = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;

    this.#close();
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.#isOpen) return;

    event.stopPropagation();
    this.#close();
    this.#trigger?.focus({ preventScroll: true });
  };
}

if (!customElements.get('custom-header-drawer')) {
  customElements.define('custom-header-drawer', CustomHeaderDrawer);
}

if (!customElements.get('custom-header-search')) {
  customElements.define('custom-header-search', CustomHeaderSearch);
}

if (!customElements.get('custom-header-nav')) {
  customElements.define('custom-header-nav', CustomHeaderNav);
}

if (!customElements.get('custom-header-language')) {
  customElements.define('custom-header-language', CustomHeaderLanguage);
}
